import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { swallow } from "../best-effort";
import { ApiError, messaging, primaryMedia, type Conversation, type Message } from "../api";
import { senderColor } from "../cockpit-ui";
import { ArrowUp, X } from "lucide-react";
import { Banner } from "./ui";
import { MediaView } from "./MediaView";
import { SenderAvatar } from "./SenderAvatar";
import { clockTime, linkify } from "../format";
import { haptic } from "../native-behaviors";

const Lightbox = lazy(() => import("./Lightbox").then((m) => ({ default: m.Lightbox })));

// Panneau de thread façon Slack : la racine + toutes ses réponses, lues à part du
// fil principal (où elles sont repliées sous un encart « 🧵 N réponses »). On
// répond DANS le thread (replyToRemoteId = remoteId de la racine).
export function ThreadPanel({
  conversation,
  root,
  initialReplies,
  onClose,
  onSent,
}: {
  conversation: Conversation;
  root: Message;
  initialReplies: Message[];
  onClose: () => void;
  onSent: () => void;
}) {
  const isDm = conversation.kind === "dm";
  const [rootMsg, setRootMsg] = useState<Message>(root);
  const [replies, setReplies] = useState<Message[]>(initialReplies);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{
    src: string;
    kind: "image" | "video";
    fileName?: string;
  } | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  // Charge le thread autoritatif (réponses au-delà de la page du fil incluses).
  // En cas d'échec, on garde les réponses passées par le fil.
  const load = useCallback(() => {
    return messaging
      .thread(root.id)
      .then((r) => {
        setRootMsg(r.root);
        setReplies(r.replies);
      })
      .catch(() => {
        /* best-effort : on reste sur les réponses initiales du fil */
      })
      .finally(() => setLoading(false));
  }, [root.id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Échap ferme le panneau (capture pour ne pas heurter d'autres handlers).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        onClose();
      }
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  // Focus sans bouger le scroll (le panneau s'ouvre en HAUT, racine visible).
  useEffect(() => {
    requestAnimationFrame(() => taRef.current?.focus({ preventScroll: true }));
  }, []);

  function who(m: Message): string {
    if (m.isOutgoing) return "Vous";
    return m.senderDisplayName || (isDm ? conversation.title : m.senderRemoteId) || "?";
  }

  async function send(e: React.FormEvent) {
    e.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;
    haptic.light().catch(swallow);
    setSending(true);
    setError(null);
    try {
      await messaging.send(conversation.id, body, undefined, rootMsg.remoteId ?? undefined);
      haptic.success().catch(swallow);
      setDraft("");
      await load();
      // Après MON envoi, on descend pour voir le message parti (sinon on reste haut).
      requestAnimationFrame(() => endRef.current?.scrollIntoView({ block: "end" }));
      onSent();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 503
          ? "Connecteur non démarré — impossible d'envoyer."
          : "Échec de l'envoi.",
      );
    } finally {
      setSending(false);
    }
  }

  function bubble(m: Message) {
    const showSender = !isDm && !m.isOutgoing && (m.senderDisplayName || m.senderRemoteId);
    return (
      <div key={m.id} className={`tpanel-row ${m.isOutgoing ? "me" : ""}`}>
        {!isDm && !m.isOutgoing && (
          <SenderAvatar
            src={
              m.senderRemoteId
                ? messaging.participantAvatarUrl(conversation.id, m.senderRemoteId)
                : null
            }
            label={m.senderDisplayName || m.senderRemoteId || "?"}
          />
        )}
        <div className={`msg ${m.isOutgoing ? "me" : ""}`}>
          {showSender && (
            <div
              className="msg-sender"
              style={{ color: senderColor(m.senderDisplayName || m.senderRemoteId || "") }}
            >
              {who(m)}
            </div>
          )}
          <MediaView m={m} onOpen={setLightbox} />
          {m.body ? (
            <div className="msg-text">
              {linkify(m.body)}
              <span className="msg-time">{clockTime(m.sentAt)}</span>
            </div>
          ) : !primaryMedia(m) ? (
            <div className="msg-text msg-deleted">
              <span>Ce message a été supprimé</span>
              <span className="msg-time">{clockTime(m.sentAt)}</span>
            </div>
          ) : (
            <div className="msg-time msg-time-block">{clockTime(m.sentAt)}</div>
          )}
          {Array.isArray(m.reactions) && m.reactions.length > 0 && (
            <div className="msg-reactions">
              {m.reactions.map((r, k) => (
                <span key={k} className={`reaction ${r.mine ? "mine" : ""}`}>
                  {r.emoji}
                  {r.count > 1 && <span>{r.count}</span>}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const count = replies.length;
  return (
    <>
      <div className="tpanel-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="tpanel" role="dialog" aria-label="Fil de discussion">
        <header className="bar thread-head">
          <strong>Fil · {conversation.title}</strong>
          <span className="head-actions">
            <button
              type="button"
              className="link"
              title="Fermer le fil"
              aria-label="Fermer le fil"
              onClick={onClose}
            >
              <X size={18} />
            </button>
          </span>
        </header>
        <div className="tpanel-body">
          {/* On n'affiche QUE les réponses du fil : le message racine reste visible
              dans le feed principal (avec son encart « 🧵 »). On le garde seulement
              en mémoire (rootMsg) pour répondre dans le bon thread. */}
          <div className="tpanel-divider">
            <span>
              {loading
                ? "…"
                : count === 0
                  ? "Aucune réponse pour l'instant"
                  : count > 1
                    ? `${count} réponses`
                    : "1 réponse"}
            </span>
          </div>
          {replies.map((m) => bubble(m))}
          {error && <Banner tone="danger">{error}</Banner>}
          <div ref={endRef} />
        </div>
        <form className="tpanel-composer" onSubmit={send}>
          <textarea
            ref={taRef}
            value={draft}
            rows={1}
            aria-label="Répondre dans le fil"
            placeholder="Répondre dans le fil…"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                e.currentTarget.form?.requestSubmit();
              }
            }}
          />
          <button
            type="submit"
            className="send"
            aria-label="Envoyer la réponse"
            disabled={sending || !draft.trim()}
          >
            {sending ? "…" : <ArrowUp size={18} />}
          </button>
        </form>
      </aside>
      {lightbox && (
        <Suspense fallback={null}>
          <Lightbox items={[lightbox]} index={0} onClose={() => setLightbox(null)} />
        </Suspense>
      )}
    </>
  );
}
